import { defineStore } from "pinia";
import { $api } from "~/composables/api";
import type { Auth, IUser, KeyLoginContext } from "~/types";

type UserState = IUser & {
  showTicketOnNextHome: boolean;
  keyLoginContext: KeyLoginContext | null;
};

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    playerId: 0,
    name: "",
    alias: "",
    email: "",
    city: "",
    mobile: "",
    avatarUrl: "",
    points: 0,
    todayPoints: 0,
    dateCreated: new Date(),
    dateUpdated: new Date(),
    accountId: 0,
    account: undefined,
    levelId: 0,
    level: undefined,
    languageId: 0,
    language: undefined,
    currentRFIDTagId: undefined,
    currentRFIDTag: undefined,
    pass: undefined,
    currentRank: 0,
    previousLogin: undefined,
    dateOfBirth: undefined,
    ageRange: "",
    gender: "",
    showTicketOnNextHome: false,
    keyLoginContext: null,
  }),
  getters: {
    loggedIn: (state): boolean => {
      return !!state.playerId && !!state.name;
    },
  },
  actions: {
    reset() {
      this.setUser({
        playerId: 0,
        name: "",
        alias: "",
        email: "",
        city: "",
        mobile: "",
        avatarUrl: "",
        dateOfBirth: undefined,
        ageRange: "",
        gender: "",
        points: 0,
        todayPoints: 0,
        dateCreated: new Date(),
        dateUpdated: new Date(),
        accountId: 0,
        account: undefined,
        levelId: 0,
        level: undefined,
        languageId: 0,
        language: undefined,
        currentRFIDTagId: undefined,
        currentRFIDTag: undefined,
        pass: undefined,
        currentRank: 0,
        previousLogin: undefined,
      } as IUser);
      this.showTicketOnNextHome = false;
      this.keyLoginContext = null;
    },
    setUser(data: IUser) {
      this.playerId = data.playerId;
      this.name = data.name;
      this.alias = data.alias;
      this.email = data.email;
      this.mobile = data.mobile;
      this.city = data.city;
      this.avatarUrl = data.avatarUrl;
      this.dateOfBirth = data.dateOfBirth;
      this.ageRange = data.ageRange ?? "";
      this.gender = data.gender;
      this.points = data.points;
      this.todayPoints = data.todayPoints;
      this.dateCreated = data.dateCreated;
      this.dateUpdated = data.dateUpdated;
      this.accountId = data.accountId;
      this.account = data.account;
      this.levelId = data.levelId;
      this.level = data.level;
      this.languageId = data.languageId;
      this.language = data.language;
      this.currentRFIDTagId = data.currentRFIDTagId;
      this.currentRFIDTag = data.currentRFIDTag;
      this.pass = data.passes ? data.passes[0] : undefined;
      this.currentRank = data.currentRank;
      this.previousLogin = data.previousLogin;
    },
    async login(alias: string, password: string) {
      try {
        this.clearKeyLoginContext();
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
        const data = await $api("/auth/player", {
          method: "POST",
          body: {
            alias,
            password,
          },
        }).then((res) => res as Auth);
        this.setUser(data.user);
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value =
          data.token;
      } catch (error) {
        console.error(error);
      }
    },
    async loginWithKey(key: string, id: number) {
      this.reset();
      useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
      const data = await $api("/auth/player/key", {
        method: "POST",
        body: { key, id },
      }).then((res) => res as Auth);
      this.setUser(data.user);
      useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value =
        data.token;
    },
    setKeyLoginContext(ctx: KeyLoginContext) {
      this.keyLoginContext = ctx;
    },
    clearKeyLoginContext() {
      this.keyLoginContext = null;
    },
    async getUserInfo(options?: { throwOnError?: boolean }) {
      try {
        const data = await $api("/auth/me").then((res) => res as IUser);
        this.setUser(data);
        return true;
      } catch (error) {
        if (options?.throwOnError) throw error;
        console.error(error);
        return false;
      }
    },
    async buyTicket(payload: {
      name: string;
      email: string;
      mobile: string;
      ageRange: string;
      gender: string;
      city: string;
      password: string;
    }) {
      const body = { ...payload, parkId: 1 };
      const data = await $api("/Player/buyTicket", {
        method: "POST",
        body,
      }).then((res) => res as Partial<Auth>);
      if (data?.token && data?.user) {
        this.setUser(data.user);
        useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value =
          data.token;
        return;
      }
      const fallback = useRuntimeConfig().public.buyTicketLoginFallback;
      if (fallback) {
        await this.login(payload.email, payload.password);
        if (this.loggedIn) return;
      }
      throw new Error("Buy ticket response missing token");
    },
    async setupProfile({
      alias,
      avatarUrl,
    }: {
      alias: string;
      avatarUrl: string;
    }) {
      await $api("/auth/me", {
        method: "PATCH",
        body: { alias, avatarUrl },
      });
      await this.getUserInfo();
    },
    flagShowTicketOnNextHome() {
      this.showTicketOnNextHome = true;
    },
    consumeShowTicketOnNextHome(): boolean {
      const v = this.showTicketOnNextHome;
      this.showTicketOnNextHome = false;
      return v;
    },
    async updateProfile({
      name,
      city,
      email,
      mobile,
      alias,
      avatarUrl,
      ageRange,
      gender,
      password,
    }: {
      name: string;
      city: string;
      email: string;
      mobile: string;
      alias: string;
      avatarUrl: string;
      ageRange: string;
      gender: string;
      password?: string;
    }) {
      const body: Record<string, unknown> = {
        name,
        city,
        email,
        mobile,
        alias,
        avatarUrl,
        ageRange,
        gender,
      };
      if (password) body.password = password;
      await $api("/auth/me", {
        method: "PATCH",
        body,
      });

      await this.getUserInfo();
    },
    async logout() {
      await $api("/auth", {
        method: "DELETE",
      });
      this.reset();
      useCookie("player-portal-token", { maxAge: 60 * 60 * 24 }).value = "";
    },
    async getAchievements(options?: { throwOnError?: boolean }) {
      try {
        return await $api("/gameAchievement");
      } catch (error) {
        if (options?.throwOnError) throw error;
        console.error(error);
        return null;
      }
    },
  },
});
